import React, { useState } from 'react';
import importService from '../../services/importService';
import ImportProgress from './ImportProgress';

const ImportForm = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importId, setImportId] = useState(null); // Pour suivre le progrès de l'importation
  
  // Gérer la sélection de fichier
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (selectedFile) {
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setError(null);
      }
    }
  };
  
  // Valider le type de fichier
  const validateFile = (file) => {
    const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const validExtensions = ['xls', 'xlsx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setError('Type de fichier non valide. Veuillez sélectionner un fichier Excel (.xls ou .xlsx).');
      return false;
    }
    
    return true;
  };
  
  // Gérer le glisser-déposer
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  // Gérer le dépôt de fichier
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
        setFileName(droppedFile.name);
        setError(null);
      }
    }
  };
  
  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Veuillez sélectionner un fichier à importer.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await importService.uploadFile(file);
      
      if (result.success) {
        // Stocker l'ID de l'importation pour suivre le progrès
        setImportId(result.importId);
        // Ne pas réinitialiser file et fileName tout de suite
        // On le fera après la fin de l'importation
      } else {
        setError('Erreur lors de l\'importation. Veuillez réessayer.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      setError(
        error.response?.data?.message || 
        'Une erreur est survenue lors de l\'importation. Veuillez réessayer.'
      );
      setLoading(false);
    }
  };
  
  // Gérer la fin de l'importation
  const handleImportComplete = (importData) => {
    setLoading(false);
    setImportId(null);
    
    if (importData.status === 'completed' || importData.status === 'success') {
      setFile(null);
      setFileName('');
      
      if (onImportComplete) {
        onImportComplete(importData);
      }
    } else if (importData.status === 'failed') {
      setError(`L'importation a échoué. ${importData.errorLog || 'Veuillez vérifier les données et réessayer.'}`);
    }
  };
  
  // Gérer l'annulation
  const handleCancel = () => {
    if (!loading) {
      setFile(null);
      setFileName('');
      setError(null);
    }
  };
  
  // Si une importation est en cours, afficher le progrès
  if (importId) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Importation en cours</h2>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Fichier: <span className="font-medium">{fileName}</span>
          </p>
        </div>
        
        <ImportProgress 
          importId={importId} 
          onComplete={handleImportComplete}
        />
        
        <div className="mt-4 text-sm text-gray-500">
          <p>L'importation peut prendre plusieurs minutes pour les gros fichiers.</p>
          <p>Vous pouvez fermer cette fenêtre, l'importation continuera en arrière-plan.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-semibold mb-4">Importer des données</h2>
      
      {error && (
        <div className="bg-red-100 p-4 rounded-md text-red-700 mb-4">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div 
          className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center mb-4 transition-colors ${
            dragActive 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 hover:border-primary-500'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <svg 
            className="h-12 w-12 text-gray-400 mb-3" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
            />
          </svg>
          
          <p className="mb-2 text-gray-700">
            Glissez-déposez un fichier Excel ici, ou
          </p>
          
          <label className="btn btn-primary cursor-pointer">
            <span>Parcourir les fichiers</span>
            <input
              type="file"
              className="hidden"
              accept=".xls,.xlsx"
              onChange={handleFileChange}
              disabled={loading}
            />
          </label>
          
          <p className="mt-2 text-sm text-gray-500">
            Formats acceptés: .xls, .xlsx
          </p>
        </div>
        
        {fileName && (
          <div className="bg-gray-50 p-3 rounded-md mb-4 flex justify-between items-center">
            <div className="flex items-center">
              <svg 
                className="h-5 w-5 text-primary-500 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                />
              </svg>
              <span className="text-gray-700">{fileName}</span>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <svg 
                className="h-5 w-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          </div>
        )}
        
        <div className="flex space-x-3">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !file}
          >
            {loading ? 'Lancement de l\'importation...' : 'Importer'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading || !file}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default ImportForm;